/**
 * RATCHET LEDGER contract — the review/receipt half of a task's story, read live.
 *
 * The runs contract (`workflow-runs.ts`) paginates `excellent_worker_runs`: one
 * row per worker process, answering "did it finish, what did it cost". This one
 * reads the DoD ledger — `excellent_ops_events` (kind `dod:blocked` /
 * `dod:parked` / `dod:classified`) and `gate_run_receipts` — and answers the
 * question a run row structurally cannot:
 *
 *     the process exited 0 — so why did the work come back?
 *
 * ONE OPERATION, NOT TWELVE. Every section of the surface is a different
 * projection of the same two tables at the same instant. Split across a dozen
 * endpoints they would drift by seconds and the totals would disagree with the
 * rows they total — a page that says "31 trips" above a list of 12 must have
 * counted both in one breath. So the whole ledger is one request-time read.
 *
 * READ-ONLY. There is no write op here and there must never be one: the ledger is
 * evidence, and a surface that renders evidence must not be able to alter it.
 *
 * RAW STRINGS SURVIVE WHOLE. `reason` carries the literal `{ok:false, reason}`
 * token the MCP gate returns (`dod-unmet`, `self-verification-blocked`, …), and
 * `proofs[]` carries the router's own sentences including the runnable remedy
 * inside them. Translation is the PAGE's job — see the components in
 * `app/styleguide/workflow-detail/`. A contract that pre-translates designs a
 * page that looks right against mock and renders nothing useful against a vault.
 *
 * Server mirror: `readRatchetLedger` in `excellent/src/agents/ratchet-db.ts`.
 * Add a field in BOTH places or it silently drops at the zod boundary.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** One refusal code: the machine token, plus the page's translation of it. */
export const refusalReasonSchema = z.object({
  /** Verbatim `{ok:false, reason}` value. Never pre-translated. */
  code: z.string(),
  label: z.string(),
  means: z.string(),
  remedy: z.string(),
  emittedBy: z.string(),
  tone: z.enum(["danger", "warning", "neutral"]),
  /** Live count over the rows named by `countedFrom`. */
  ledgerCount: z.number(),
  /** The predicate the count was taken over, in the reader's own words. Shipped
   *  so a surface can say what a ZERO means: a code whose rows the query cannot
   *  see is not a code that never fired, and the two must not render alike. */
  countedFrom: z.string(),
});
export type RefusalReasonDto = z.infer<typeof refusalReasonSchema>;

/** One bounce: a task arriving at review and being sent back. */
export const reviewTripSchema = z.object({
  opsEventId: z.number(),
  taskId: z.number().nullable(),
  title: z.string().nullable(),
  attempt: z.number().nullable(),
  /** 12-char tree hash — a criterion is met at a TREE, not on a task. */
  tree: z.string().nullable(),
  source: z.string().nullable(),
  reason: z.string().nullable(),
  unmet: z.number().nullable(),
  shipper: z.string().nullable(),
  verifier: z.string().nullable(),
  /** Verbatim proof strings, remedy included. Rendered whole, never truncated. */
  proofs: z.array(z.string()),
  /** `parked` only — what the router already attempted before giving up. */
  tried: z.array(z.string()),
  reviewTrips: z.number().nullable(),
  limit: z.number().nullable(),
  /** `parked` is a different ending from `blocked`: retry is bounded, and when
   *  the bound is hit the router stops rather than loop. */
  kind: z.enum(["blocked", "parked"]),
  at: z.string(),
  /** At least one proof here is about a check that RAN and exited non-zero —
   *  as opposed to a receipt never minted or one binding a different tree.
   *  Classified server-side from the proof strings, so a row badge and the
   *  ledger-wide tile are two renderings of one predicate. */
  hasRedCheck: z.boolean(),
});
export type ReviewTripDto = z.infer<typeof reviewTripSchema>;

/** Receipt rollup for one task. */
export const taskReceiptSchema = z.object({
  taskId: z.number(),
  title: z.string().nullable(),
  status: z.string().nullable(),
  shipper: z.string().nullable(),
  total: z.number(),
  pass: z.number(),
  fail: z.number(),
  /** Failing receipts that are delta-mode — red baseline, MET criterion. Without
   *  this the `fail` column libels correct, landed work. */
  deltaFail: z.number(),
  hasDelta: z.boolean(),
  latestAt: z.string().nullable(),
});
export type TaskReceiptDto = z.infer<typeof taskReceiptSchema>;

/** A delta receipt: the gate red at both ends, the criterion met anyway. */
export const deltaReceiptSchema = z.object({
  id: z.number(),
  taskId: z.number(),
  title: z.string().nullable(),
  command: z.string(),
  /** Non-zero on every one. That is the point, not a defect. */
  exitCode: z.number(),
  baseCount: z.number().nullable(),
  headCount: z.number().nullable(),
  /** Column exists (v158); null on rows minted before it was populated. */
  deltaCount: z.number().nullable(),
  baseCommit: z.string().nullable(),
  tree: z.string().nullable(),
  baseTree: z.string().nullable(),
  at: z.string(),
});
export type DeltaReceiptDto = z.infer<typeof deltaReceiptSchema>;

/** Same task, same command, same tree — two different exit codes. */
export const flakePairSchema = z.object({
  taskId: z.number(),
  command: z.string(),
  tree: z.string(),
  binding: z.string().nullable(),
  mode: z.string().nullable(),
  firstId: z.number(),
  firstExit: z.number(),
  firstAt: z.string(),
  secondId: z.number(),
  secondExit: z.number(),
  secondAt: z.string(),
});
export type FlakePairDto = z.infer<typeof flakePairSchema>;

export const workClassSchema = z.object({
  key: z.string(),
  paths: z.number(),
  events: z.number(),
});
export type WorkClassDto = z.infer<typeof workClassSchema>;

/**
 * A mutation battery as a MACHINE recorded it — an `excellent_ops_events` row,
 * not a sentence parsed out of a task description.
 *
 * Empty on today's vault: nothing writes the event yet. The array ships anyway
 * so the surface renders a labelled empty state naming the emitter that has to
 * land, and so the numbers a tile shows can only ever come from a row a machine
 * wrote. The prose half lives in `evidenceTallySchema` and is quoted, never
 * counted.
 */
export const mutationBatterySchema = z.object({
  opsEventId: z.number(),
  taskId: z.number().nullable(),
  title: z.string(),
  command: z.string().nullable(),
  killed: z.number().nullable(),
  planted: z.number().nullable(),
  /** Whether a null control was staged at all — the only evidence the kill
   *  signal is real rather than a crash wearing a kill's clothes. */
  hasNullControl: z.boolean(),
  nullControlSurvived: z.boolean(),
  at: z.string(),
});
export type MutationBatteryDto = z.infer<typeof mutationBatterySchema>;

/**
 * A tally line QUOTED from a task's own ship evidence.
 *
 * Deliberately carries no parsed numbers. Prose cannot be attributed reliably —
 * a review note citing another task's tally reads identically to a result — so
 * the honest rendering is the sentence, credited to the task it was found in,
 * feeding no tile and no cross-battery total.
 */
export const evidenceTallySchema = z.object({
  taskId: z.number(),
  title: z.string(),
  status: z.string().nullable(),
  /** Verbatim. No number is lifted out of it. */
  quote: z.string(),
  at: z.string().nullable(),
});
export type EvidenceTallyDto = z.infer<typeof evidenceTallySchema>;

/** A battery, gate or criterion measured to be incapable of failing. */
export const sensorHoleSchema = z.object({
  opsEventId: z.number(),
  severity: z.string(),
  status: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  foundBy: z.string().nullable(),
  at: z.string(),
  resolvedAt: z.string().nullable(),
});
export type SensorHoleDto = z.infer<typeof sensorHoleSchema>;

/** A judge's ruling on a milestone, recorded as a structured ops event.
 *  Replaces the prose-scraping reader (JUDGMENT RECORDED / UPGRADE PATH markers). */
export const judgeRulingSchema = z.object({
  opsEventId: z.number(),
  taskId: z.number().nullable(),
  title: z.string(),
  ruling: z.enum(["MET", "PARTIAL", "NOT-MET"]).nullable(),
  upgradeBar: z.string().nullable(),
  judgeId: z.string().nullable(),
  at: z.string(),
});
export type JudgeRulingDto = z.infer<typeof judgeRulingSchema>;

export const strongFormSchema = z.object({
  opsEventId: z.number(),
  title: z.string(),
  detail: z.string().nullable(),
  at: z.string(),
});
export type StrongFormDto = z.infer<typeof strongFormSchema>;

export const usageTierSchema = z.object({
  threshold: z.number(),
  key: z.string(),
  label: z.string(),
  behaviour: z.string(),
  /** Live fire count. Zero until the tiers are instrumented — and that zero is
   *  the finding, not a placeholder. */
  fires: z.number(),
});

export const usageWindowSchema = z.object({
  tiers: z.array(usageTierSchema),
  meteredCalls: z.number(),
  meteredCostUsd: z.number(),
  lastCall: z
    .object({
      surface: z.string().nullable(),
      provider: z.string().nullable(),
      model: z.string().nullable(),
      inputTokens: z.number().nullable(),
      outputTokens: z.number().nullable(),
      cachedTokens: z.number().nullable(),
      costUsd: z.number().nullable(),
      at: z.string().nullable(),
    })
    .nullable(),
  tierEvents: z.number(),
});
export type UsageWindowDto = z.infer<typeof usageWindowSchema>;

export const ratchetTotalsSchema = z.object({
  receiptRows: z.number(),
  receiptsAbsolute: z.number(),
  receiptsDelta: z.number(),
  receiptsWithDeltaCount: z.number(),
  blockedTrips: z.number(),
  parkedTrips: z.number(),
  classifyEvents: z.number(),
  distinctAgents: z.number(),
  /** Proof shapes, counted across EVERY trip in the ledger. A trip can carry
   *  several at once, so these do not partition blocked + parked. They exist
   *  because a surface must not assert the split it cannot measure — the
   *  rejected first cut hardcoded "caught by a red check: 0" beside three live
   *  values, and rows on the same card contradicted it. */
  tripsWithRedCheck: z.number(),
  tripsWithStaleTree: z.number(),
  tripsWithMissingReceipt: z.number(),
  tripsWithDirtyBinding: z.number(),
  /** Tasks that tripped review carrying no receipt at all. */
  tasksWithNoReceipts: z.number(),
  /** Tasks carrying a non-zero DELTA receipt — red gate, met criterion. */
  tasksWithDeltaFail: z.number(),
});
export type RatchetTotalsDto = z.infer<typeof ratchetTotalsSchema>;

export const ratchetLedgerSchema = z.object({
  /** When this read happened. The surface stamps it so a reader can tell a live
   *  page from a cached one. */
  pulledAt: z.string(),
  refusals: z.array(refusalReasonSchema),
  trips: z.array(reviewTripSchema),
  receipts: z.array(taskReceiptSchema),
  deltaReceipts: z.array(deltaReceiptSchema),
  flakePairs: z.array(flakePairSchema),
  workClasses: z.array(workClassSchema),
  mutations: z.array(mutationBatterySchema),
  evidenceTallies: z.array(evidenceTallySchema),
  sensorHoles: z.array(sensorHoleSchema),
  rulings: z.array(judgeRulingSchema),
  strongForm: z.array(strongFormSchema),
  usage: usageWindowSchema,
  totals: ratchetTotalsSchema,
});
export type RatchetLedgerDto = z.infer<typeof ratchetLedgerSchema>;

export const ratchetLedgerRead: Operation<void, void, void, RatchetLedgerDto> = {
  method: "GET",
  path: () => "/api/agents/ratchet",
  response: ratchetLedgerSchema,
};

export const ratchetLedger = { read: ratchetLedgerRead };
